import { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { PaperPlaneRight, User, Warning, CalendarCheck } from "@phosphor-icons/react";
import { toast } from "sonner";
import dayjs from "dayjs";
import "dayjs/locale/fr";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.locale("fr");
const parisTime = (t) => dayjs(t).tz("Europe/Paris");

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BG_URL =
  "https://images.unsplash.com/photo-1722954249988-452c1f4f62ee?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzZ8MHwxfHNlYXJjaHwyfHxjYWxtJTIwYWJzdHJhY3QlMjBuYXR1cmUlMjBiYWNrZ3JvdW5kfGVufDB8fHx8MTc4NDI0MTMwOHww&ixlib=rb-4.1.0&q=85";

const LOGO_URL =
  "https://customer-assets-lxgj4vgw.emergentagent.net/job_ai-client-assist/artifacts/5xamlug5_Logo%20VMLH-TCC%20V2.png";

const AVATAR_URL = "/chloe-avatar.png";

const WELCOME = {
  role: "assistant",
  content:
    "Bonjour, je suis Chloé, l’assistante numérique du Cabinet Alésia Thérapie. Je peux vous renseigner sur le cabinet et vous aider à gérer vos rendez-vous.\nSi vous souhaitez échanger directement avec le thérapeute, je peux bien sûr lui transmettre votre message. Il vous répondra dès que possible. Un peu de délai peut parfois être nécessaire, car il veille à rester pleinement disponible pour chaque personne pendant les séances.\nDe mon côté, je suis là pour simplifier vos démarches et vous offrir un accueil attentif.",
  slots: null,
};

function getSessionId() {
  let id = sessionStorage.getItem("chat_session_id");
  if (!id) {
    id = crypto.randomUUID();
    sessionStorage.setItem("chat_session_id", id);
  }
  return id;
}

const SlotPills = ({ slots, mock, eventTypeName }) => {
  const [showAll, setShowAll] = useState(false);
  const visible = showAll ? slots : slots.slice(0, 6);
  return (
  <div className="mt-3 flex flex-wrap gap-2" data-testid="slot-pills">
    {eventTypeName && (
      <span data-testid="slot-event-type" className="w-full text-xs font-semibold text-[#24643D]">
        {eventTypeName}
      </span>
    )}
    {visible.map((s, i) => {
      const d = parisTime(s.start_time);
      return (
        <button
          key={i}
          data-testid={`slot-pill-${i}`}
          onClick={() => {
            if (s.scheduling_url) {
              window.open(s.scheduling_url, "_blank", "noopener,noreferrer");
            } else {
              toast.info("Créneau indicatif — le cabinet confirmera votre demande.", {
                description: d.format("dddd D MMMM [à] HH[h]mm"),
              });
            }
          }}
          className="flex items-center gap-2 rounded-full border border-[#24643D]/30 bg-[#24643D]/5 px-4 py-2 text-sm font-medium text-[#1B4A2E] transition-[background-color,transform,box-shadow] hover:-translate-y-[1px] hover:bg-[#24643D] hover:text-white hover:shadow-md"
        >
          <CalendarCheck size={16} weight="duotone" />
          {d.format("ddd D MMM · HH[h]mm")}
        </button>
      );
    })}
    {slots.length > 6 && (
      <button
        data-testid="toggle-more-slots"
        onClick={() => setShowAll(!showAll)}
        className="rounded-full border border-dashed border-[#24643D]/40 px-4 py-2 text-sm text-[#24643D] transition-colors hover:bg-[#24643D]/10"
      >
        {showAll ? "Voir moins" : `Voir plus de créneaux (${slots.length - 6})`}
      </button>
    )}
    {mock && (
      <span className="w-full text-xs text-[#5C6B60] italic">
        Créneaux indicatifs — confirmation par le cabinet.
      </span>
    )}
  </div>
  );
};

const RescheduleCard = ({ data }) => {
  const [selected, setSelected] = useState(null);
  const [working, setWorking] = useState(false);
  const [doneMsg, setDoneMsg] = useState(null);
  const [showAll, setShowAll] = useState(false);

  if (!data.found) {
    return (
      <div data-testid="reschedule-not-found" className="mt-3 rounded-xl border border-[#E2DFD8] bg-[#F9F8F6] p-4 text-sm text-[#5C6B60]">
        Aucun rendez-vous à venir n'a été trouvé pour <strong>{data.email}</strong>. Vérifiez
        l'adresse e-mail utilisée lors de la réservation.
      </div>
    );
  }

  const apt = data.appointment;
  const aptDate = parisTime(apt.start_time);

  if (doneMsg) {
    return (
      <div data-testid="reschedule-success" className="mt-3 rounded-xl border border-[#24643D]/30 bg-[#24643D]/5 p-4 text-sm text-[#1E2A22]">
        <p className="flex items-center gap-2 font-medium text-[#24643D]">
          <CalendarCheck size={18} weight="duotone" /> Rendez-vous reprogrammé
        </p>
        <p className="mt-1">{doneMsg}</p>
      </div>
    );
  }

  const executeReschedule = async () => {
    setWorking(true);
    try {
      const res = await fetch(`${API}/reschedule/execute`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: data.email,
          event_uuid: apt.event_uuid,
          new_start_time: selected.start_time,
        }),
      });
      const result = await res.json();
      if (result.ok) {
        toast.success("Rendez-vous reprogrammé", { description: result.message });
        setDoneMsg(
          `Nouveau créneau : ${parisTime(selected.start_time).format("dddd D MMMM [à] HH[h]mm")}. ${result.message}`
        );
      } else {
        toast.error(result.message);
        if (result.fallback_url) window.open(result.fallback_url, "_blank", "noopener,noreferrer");
      }
    } catch {
      toast.error("Erreur technique lors de la reprogrammation.");
    } finally {
      setWorking(false);
    }
  };

  return (
    <div data-testid="reschedule-card" className="mt-3 rounded-xl border border-[#E2DFD8] bg-[#F9F8F6] p-4">
      <p className="text-sm text-[#1E2A22]">
        <span className="font-semibold">Rendez-vous trouvé :</span> {apt.event_name} —{" "}
        {aptDate.format("dddd D MMMM [à] HH[h]mm")}
      </p>
      <p className="mt-2 text-xs font-semibold uppercase tracking-[0.15em] text-[#5C6B60]">
        Choisissez un nouveau créneau
      </p>
      <div className="mt-2 flex flex-wrap gap-2">
        {(showAll ? data.slots : data.slots.slice(0, 6)).map((s, i) => (
          <button
            key={i}
            data-testid={`reschedule-slot-${i}`}
            onClick={() => setSelected(s)}
            className={`flex items-center gap-2 rounded-full border px-4 py-2 text-sm font-medium transition-[background-color,transform] hover:-translate-y-[1px] ${
              selected?.start_time === s.start_time
                ? "border-[#24643D] bg-[#24643D] text-white"
                : "border-[#24643D]/30 bg-white text-[#1B4A2E] hover:bg-[#24643D]/10"
            }`}
          >
            <CalendarCheck size={16} weight="duotone" />
            {parisTime(s.start_time).format("ddd D MMM · HH[h]mm")}
          </button>
        ))}
        {data.slots.length > 6 && (
          <button
            data-testid="reschedule-toggle-more"
            onClick={() => setShowAll(!showAll)}
            className="rounded-full border border-dashed border-[#24643D]/40 px-4 py-2 text-sm text-[#24643D] transition-colors hover:bg-[#24643D]/10"
          >
            {showAll ? "Voir moins" : `Voir plus (${data.slots.length - 6})`}
          </button>
        )}
      </div>
      {selected && (
        <div className="mt-4 flex flex-wrap items-center gap-3">
          <button
            data-testid="reschedule-confirm"
            onClick={executeReschedule}
            disabled={working}
            className="rounded-full bg-[#24643D] px-5 py-2.5 text-sm font-medium text-white shadow-sm transition-[background-color,transform] hover:-translate-y-[1px] hover:bg-[#1B4A2E] disabled:opacity-50"
          >
            {working ? "Reprogrammation…" : "Confirmer la reprogrammation"}
          </button>
          {apt.reschedule_url && (
            <button
              data-testid="reschedule-via-calendly"
              onClick={() => window.open(apt.reschedule_url, "_blank", "noopener,noreferrer")}
              className="rounded-full border border-[#24643D]/40 bg-white px-5 py-2.5 text-sm font-medium text-[#1B4A2E] transition-[background-color,transform] hover:-translate-y-[1px] hover:bg-[#24643D]/10"
            >
              Reprogrammer via Calendly
            </button>
          )}
        </div>
      )}
      {data.mock && (
        <p className="mt-3 text-xs italic text-[#5C6B60]">
          Mode démo — agenda Calendly non connecté, la reprogrammation sera simulée.
        </p>
      )}
    </div>
  );
};

const CancelCard = ({ data }) => {
  const [working, setWorking] = useState(false);
  const [doneMsg, setDoneMsg] = useState(null);

  if (!data.found) {
    return (
      <div data-testid="cancel-not-found" className="mt-3 rounded-xl border border-[#E2DFD8] bg-[#F9F8F6] p-4 text-sm text-[#5C6B60]">
        Aucun rendez-vous à venir n'a été trouvé pour <strong>{data.email}</strong>. Vérifiez
        l'adresse e-mail utilisée lors de la réservation.
      </div>
    );
  }

  const apt = data.appointment;

  if (doneMsg) {
    return (
      <div data-testid="cancel-success" className="mt-3 rounded-xl border border-[#E07A5F]/40 bg-[#E07A5F]/10 p-4 text-sm text-[#1E2A22]">
        <p className="flex items-center gap-2 font-medium text-[#B3452E]">
          <CalendarCheck size={18} weight="duotone" /> Rendez-vous annulé
        </p>
        <p className="mt-1">{doneMsg}</p>
      </div>
    );
  }

  const executeCancel = async () => {
    setWorking(true);
    try {
      const res = await fetch(`${API}/cancel/execute`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: data.email, event_uuid: apt.event_uuid }),
      });
      const result = await res.json();
      if (result.ok) {
        toast.success("Rendez-vous annulé", { description: result.message });
        setDoneMsg(result.message);
      } else {
        toast.error(result.message);
        if (result.fallback_url) window.open(result.fallback_url, "_blank", "noopener,noreferrer");
      }
    } catch {
      toast.error("Erreur technique lors de l'annulation.");
    } finally {
      setWorking(false);
    }
  };

  return (
    <div data-testid="cancel-card" className="mt-3 rounded-xl border border-[#E2DFD8] bg-[#F9F8F6] p-4">
      <p className="text-sm text-[#1E2A22]">
        <span className="font-semibold">Rendez-vous trouvé :</span> {apt.event_name} —{" "}
        {parisTime(apt.start_time).format("dddd D MMMM [à] HH[h]mm")}
      </p>
      <div className="mt-4 flex flex-wrap items-center gap-3">
        <button
          data-testid="cancel-confirm"
          onClick={executeCancel}
          disabled={working}
          className="rounded-full bg-[#B3452E] px-5 py-2.5 text-sm font-medium text-white shadow-sm transition-[background-color,transform] hover:-translate-y-[1px] hover:bg-[#93361F] disabled:opacity-50"
        >
          {working ? "Annulation…" : "Confirmer l'annulation"}
        </button>
        {apt.cancel_url && (
          <button
            data-testid="cancel-via-calendly"
            onClick={() => window.open(apt.cancel_url, "_blank", "noopener,noreferrer")}
            className="rounded-full border border-[#B3452E]/40 bg-white px-5 py-2.5 text-sm font-medium text-[#B3452E] transition-[background-color,transform] hover:-translate-y-[1px] hover:bg-[#B3452E]/10"
          >
            Annuler via Calendly
          </button>
        )}
      </div>
      {data.mock && (
        <p className="mt-3 text-xs italic text-[#5C6B60]">
          Mode démo — agenda Calendly non connecté, l'annulation sera simulée.
        </p>
      )}
    </div>
  );
};

const Bubble = ({ msg }) => {
  if (msg.author === "system") {
    return (
      <div className="flex justify-center" data-testid="system-notice">
        <p className="max-w-[92%] rounded-full bg-[#1E2A22]/5 px-4 py-2 text-center text-xs italic text-[#5C6B60]">
          {msg.content}
        </p>
      </div>
    );
  }
  const isUser = msg.role === "user";
  const isTherapist = msg.author === "therapist";
  const text = msg.content.replace(/\[SLOTS(?::\d+)?\]/g, "").replace(/\[(RESCHEDULE|CANCEL):[^\]]+\]/g, "").replace(/\[RELAY\]/g, "").trim();
  return (
    <motion.div
      initial={{ opacity: 0, y: 10, scale: 0.98 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{ type: "spring", damping: 20, stiffness: 100 }}
      className={`flex gap-3 ${isUser ? "flex-row-reverse" : ""}`}
    >
      <div
        className={`mt-1 flex h-8 w-8 shrink-0 items-center justify-center overflow-hidden rounded-full ${
          isUser ? "bg-[#E07A5F]/15 text-[#E07A5F]" : "bg-[#24643D]/10"
        }`}
      >
        {isUser ? (
          <User size={16} weight="duotone" />
        ) : isTherapist ? (
          <img src={LOGO_URL} alt="Praticien" data-testid="therapist-avatar" className="h-full w-full bg-white object-contain p-0.5" />
        ) : (
          <img src={AVATAR_URL} alt="Chloé" data-testid="chloe-avatar" className="h-full w-full object-cover" />
        )}
      </div>
      <div
        data-testid={isUser ? "user-bubble" : isTherapist ? "therapist-bubble" : "assistant-bubble"}
        className={`max-w-[80%] rounded-2xl px-5 py-3.5 text-[15px] leading-relaxed ${
          isUser
            ? "rounded-tr-sm bg-[#24643D] text-white"
            : isTherapist
              ? "rounded-tl-sm border border-[#24643D]/40 bg-[#EAF2ED] text-[#1E2A22]"
              : "rounded-tl-sm border border-[#E2DFD8] bg-white text-[#1E2A22]"
        }`}
      >
        {isTherapist && (
          <p className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-[#24643D]">
            Victor Lopez-Hernandez · praticien
          </p>
        )}
        <p className="whitespace-pre-wrap">{text}</p>
        {msg.slots && msg.slots.length > 0 && <SlotPills slots={msg.slots} mock={msg.mock} eventTypeName={msg.eventTypeName} />}
        {msg.reschedule && <RescheduleCard data={msg.reschedule} />}
        {msg.cancel && <CancelCard data={msg.cancel} />}
      </div>
    </motion.div>
  );
};

export default function PatientChat({ widget = false }) {
  const [messages, setMessages] = useState([WELCOME]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [showDisclaimer, setShowDisclaimer] = useState(true);
  const [humanMode, setHumanMode] = useState(false);
  const humanModeRef = useRef(false);
  const lastPollRef = useRef(new Date().toISOString());
  const seenIds = useRef(new Set());
  const scrollRef = useRef(null);
  const sessionId = useRef(getSessionId());
  const nudgeTimer = useRef(null);
  const nudgeSent = useRef(false);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, streaming]);

  useEffect(() => () => clearTimeout(nudgeTimer.current), []);

  useEffect(() => {
    const iv = setInterval(async () => {
      if (document.visibilityState !== "visible") return;
      try {
        const res = await fetch(
          `${API}/chat/${sessionId.current}/updates?after=${encodeURIComponent(lastPollRef.current)}`
        );
        const data = await res.json();
        humanModeRef.current = data.mode === "human";
        setHumanMode(humanModeRef.current);
        const fresh = (data.messages || []).filter((m) => !seenIds.current.has(m.id));
        if (fresh.length) {
          fresh.forEach((m) => seenIds.current.add(m.id));
          lastPollRef.current = fresh[fresh.length - 1].created_at;
          setMessages((prev) => [
            ...prev,
            ...fresh.map((m) => ({ role: "assistant", author: m.author, content: m.content })),
          ]);
        }
      } catch {
        /* silencieux */
      }
    }, 4000);
    return () => clearInterval(iv);
  }, []);

  const scheduleNudge = () => {
    clearTimeout(nudgeTimer.current);
    if (nudgeSent.current || humanModeRef.current) return;
    nudgeTimer.current = setTimeout(async () => {
      if (nudgeSent.current || humanModeRef.current) return;
      nudgeSent.current = true;
      try {
        const res = await fetch(`${API}/chat/nudge`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ session_id: sessionId.current }),
        });
        const data = await res.json();
        setMessages((m) => [
          ...m,
          { role: "assistant", content: data.content, slots: data.slots, mock: data.mock },
        ]);
      } catch {
        /* silencieux */
      }
    }, 120000);
  };

  const send = async () => {
    const text = input.trim();
    if (!text || streaming) return;
    clearTimeout(nudgeTimer.current);
    setInput("");
    setMessages((m) => [...m, { role: "user", content: text }]);
    setStreaming(true);
    setMessages((m) => [...m, { role: "assistant", content: "", slots: null, pending: true }]);
    let slotsShown = false;

    try {
      const res = await fetch(`${API}/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionId.current, message: text }),
      });
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split("\n\n");
        buffer = parts.pop();
        for (const part of parts) {
          if (!part.startsWith("data: ")) continue;
          const data = JSON.parse(part.slice(6));
          if (data.done && data.human_mode) {
            humanModeRef.current = true;
            setHumanMode(true);
            setMessages((m) => m.slice(0, -1));
            continue;
          }
          setMessages((m) => {
            const copy = [...m];
            const last = { ...copy[copy.length - 1] };
            if (data.delta) last.content += data.delta;
            if (data.done) {
              last.pending = false;
              if (data.has_slots && data.slots) {
                last.slots = data.slots;
                last.mock = data.mock;
                last.eventTypeName = data.event_type_name;
                slotsShown = true;
              }
              if (data.reschedule) {
                last.reschedule = data.reschedule;
                slotsShown = true;
              }
              if (data.cancel) {
                last.cancel = data.cancel;
                slotsShown = true;
              }
            }
            copy[copy.length - 1] = last;
            return copy;
          });
        }
      }
    } catch (e) {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = {
          role: "assistant",
          content: "Désolée, une erreur est survenue. Merci de réessayer.",
        };
        return copy;
      });
    } finally {
      setStreaming(false);
    }
  };

  return (
    <div className={`relative flex h-screen flex-col overflow-hidden ${widget ? "bg-[#F9F8F6]" : ""}`}>
      {!widget && (
        <>
          <div
            className="absolute inset-0 bg-cover bg-center"
            style={{ backgroundImage: `url(${BG_URL})` }}
          />
          <div className="absolute inset-0 bg-[#1E2A22]/20 backdrop-blur-sm" />
        </>
      )}

      <AnimatePresence>
        {showDisclaimer && (
          <motion.div
            role="alert"
            data-testid="disclaimer-banner"
            initial={{ y: -60 }}
            animate={{ y: 0 }}
            exit={{ y: -80, opacity: 0 }}
            className="relative z-20 flex items-center justify-center gap-3 bg-[#24643D] px-4 py-2.5 text-center text-[13px] font-medium text-white"
          >
            <Warning size={18} weight="fill" className="shrink-0" />
            <span>
              L'assistante numérique automatisée Chloé est un chatbot. En cas d'urgence ou de
              détresse : <strong>3114</strong> (prévention suicide, 24h/24), <strong>15</strong> ou{" "}
              <strong>112</strong>.
            </span>
            <button
              data-testid="dismiss-disclaimer"
              onClick={() => setShowDisclaimer(false)}
              className="ml-2 rounded-full px-2 py-0.5 text-white/80 transition-colors hover:bg-white/15 hover:text-white"
            >
              ✕
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      <div className={`relative z-10 flex flex-1 items-center justify-center overflow-hidden ${widget ? "p-0" : "p-3 sm:p-6"}`}>
        <div className={`flex h-full w-full flex-col overflow-hidden ${widget ? "bg-white" : "max-w-2xl rounded-2xl border border-white/40 bg-white/80 shadow-[0_8px_32px_rgba(0,0,0,0.12)] backdrop-blur-2xl"}`}>
          <header className={`flex items-center gap-3 border-b border-[#E2DFD8]/70 sm:gap-4 ${widget ? "px-5 py-3" : "px-4 py-3 sm:px-8 sm:py-5"}`}>
            <img
              src={LOGO_URL}
              alt="Logo Cabinet Alésia Thérapie"
              data-testid="cabinet-logo"
              className={`shrink-0 rounded-full border border-[#E2DFD8] bg-white object-contain p-1 ${widget ? "h-10 w-10" : "h-10 w-10 sm:h-14 sm:w-14"}`}
            />
            <div>
              <h1 className={`font-serif-display font-semibold tracking-tight text-[#1E2A22] ${widget ? "text-xl" : "text-xl sm:text-3xl"}`}>
                Cabinet Alésia Thérapie
              </h1>
              <p className={`text-[#5C6B60] ${widget ? "text-xs" : "mt-0.5 text-xs sm:mt-1 sm:text-sm"}`}>
                Chloé · Assistante numérique — rendez-vous & informations pratiques
              </p>
            </div>
          </header>

          {humanMode && (
            <div
              data-testid="human-mode-banner"
              className="flex items-center justify-center gap-2 border-b border-[#24643D]/20 bg-[#24643D]/10 px-4 py-2 text-xs font-medium text-[#1B4A2E]"
            >
              <img src={LOGO_URL} alt="" className="h-4 w-4 rounded-full bg-white object-contain" />
              Le praticien vous répond personnellement
            </div>
          )}

          <div ref={scrollRef} className={`chat-scroll flex-1 space-y-5 overflow-y-auto py-6 ${widget ? "px-4" : "px-4 sm:px-8"}`}>
            {messages.map((msg, i) =>
              msg.pending && !msg.content ? (
                <div key={i} className="flex gap-3">
                  <div className="mt-1 flex h-8 w-8 items-center justify-center overflow-hidden rounded-full bg-[#24643D]/10">
                    <img src={AVATAR_URL} alt="Chloé" className="h-full w-full object-cover" />
                  </div>
                  <div className="flex items-center gap-1.5 rounded-2xl rounded-tl-sm border border-[#E2DFD8] bg-white px-5 py-4">
                    <span className="typing-dot h-2 w-2 rounded-full bg-[#24643D]" />
                    <span className="typing-dot h-2 w-2 rounded-full bg-[#24643D]" />
                    <span className="typing-dot h-2 w-2 rounded-full bg-[#24643D]" />
                  </div>
                </div>
              ) : (
                <Bubble key={i} msg={msg} />
              )
            )}
          </div>

          <div className={`border-t border-[#E2DFD8]/70 ${widget ? "p-3" : "p-4 sm:p-6"}`}>
            <div className="flex items-end gap-3">
              <textarea
                data-testid="chat-input"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    send();
                  }
                }}
                rows={1}
                placeholder="Écrivez votre message…"
                className="max-h-32 flex-1 resize-none rounded-xl border border-[#E2DFD8] bg-white px-4 py-3 text-[15px] text-[#1E2A22] placeholder:text-[#5C6B60]/60 focus:border-[#24643D] focus:outline-none focus:ring-2 focus:ring-[#24643D]/20"
              />
              <button
                data-testid="send-button"
                onClick={send}
                disabled={streaming || !input.trim()}
                className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-[#24643D] text-white shadow-md transition-[background-color,transform,box-shadow] hover:-translate-y-[1px] hover:bg-[#1B4A2E] hover:shadow-lg disabled:opacity-40 disabled:hover:translate-y-0"
              >
                <PaperPlaneRight size={20} weight="fill" />
              </button>
            </div>
            <p className="mt-3 text-center text-[11px] text-[#5C6B60]/80">
              Chloé ne fournit aucun conseil médical ou thérapeutique.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
